import { collectionSchema, type Collection } from "@shared/schemas/common";
import {
  productDetailSchema,
  productRowSchema,
  type AddMemberRequest,
  type CreateProductRequest,
  type ProductDetail,
  type ProductRow,
  type ProductStatusName,
  type UpdateProductRequest,
} from "@shared/schemas/product";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  noContentSchema,
  queryString,
} from "@client/lib/api";

/**
 * The product half of docs/06-api.md, typed by the same zod schemas the server
 * validates with. Every response is parsed, so a contract change surfaces here
 * rather than as an undefined three components deep.
 */

const productCollectionSchema = collectionSchema(productRowSchema);

export interface ProductListFilters {
  status?: ProductStatusName;
  q?: string;
  mine?: boolean;
  limit?: number;
  offset?: number;
}

export const listProducts = (filters: ProductListFilters = {}): Promise<Collection<ProductRow>> =>
  apiGet(
    `/products${queryString({
      status: filters.status,
      q: filters.q,
      // Only sent when true: `?mine=false` and no `mine` mean the same thing and
      // sending both would give the cache two keys for one result.
      mine: filters.mine ? "true" : undefined,
      limit: filters.limit,
      offset: filters.offset,
    })}`,
    productCollectionSchema,
  );

/** `idOrSlug`, because the URL carries the slug and the mutations carry the id. */
export const getProduct = (idOrSlug: string): Promise<ProductDetail> =>
  apiGet(`/products/${encodeURIComponent(idOrSlug)}`, productDetailSchema);

export const createProduct = (input: CreateProductRequest): Promise<ProductDetail> =>
  apiPost("/products", productDetailSchema, input);

export const updateProduct = (id: string, input: UpdateProductRequest): Promise<ProductDetail> =>
  apiPatch(`/products/${encodeURIComponent(id)}`, productDetailSchema, input);

export const archiveProduct = (id: string, restore = false): Promise<ProductDetail> =>
  apiPost(
    `/products/${encodeURIComponent(id)}/archive${queryString({ restore: restore ? "true" : undefined })}`,
    productDetailSchema,
  );

export const addProductMember = (id: string, input: AddMemberRequest): Promise<ProductDetail> =>
  apiPost(`/products/${encodeURIComponent(id)}/members`, productDetailSchema, input);

/** 204, unlike its POST counterpart — the caller refetches the product. */
export const removeProductMember = (id: string, userId: string): Promise<undefined> =>
  apiDelete(
    `/products/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    noContentSchema,
  );

export const transferProduct = (id: string, ownerId: string): Promise<ProductDetail> =>
  apiPost(`/products/${encodeURIComponent(id)}/transfer`, productDetailSchema, { ownerId });
